import * as store from '@attain-apps/fe/web/main/lib/store'

import * as models from './models'
import type { State } from './state'

/**
 * Core actions for this feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /** Alters the visibility of the File Finder. */
    displayFinder: store.actions.standalone({
      data: store.actions.Data.asIs<boolean | 'toggle'>(),
      reducer: ({ state, data }): State => ({
        ...state,
        finder: {
          ...state.finder,
          isOpen: data === 'toggle' ? !state.finder.isOpen : data,
        },
      }),
    }),
    download: store.actions.request({
      input: store.actions.Data.asIs<models.FileRequest.ForDownload>(),
      output: store.actions.Data.asIs<string>(),
      reducers: {
        cancellation: ({ state, data: { request } }): State => {
          const newLookup = { ...state.downloadLookup }
          delete newLookup[models.FileRequest.getKey(request)]
          return {
            ...state,
            downloadLookup: newLookup,
          }
        },
        error: ({ state, data: { request, error } }): State => ({
          ...state,
          downloadLookup: {
            ...state.downloadLookup,
            [models.FileRequest.getKey(request)]: {
              error,
            },
          },
        }),
        request: ({ state, data }): State => ({
          ...state,
          downloadLookup: {
            ...state.downloadLookup,
            [models.FileRequest.getKey(data)]: { isDownloading: true },
          },
        }),
        success: ({ state, data: { request } }): State => {
          const newLookup = { ...state.downloadLookup }
          delete newLookup[models.FileRequest.getKey(request)]
          return {
            ...state,
            downloadLookup: newLookup,
          }
        },
      },
    }),
    /**
     * Loads the files the File Finder lists for the active zone: shared zone
     * files plus the current user's private files.
     */
    loadFinderFiles: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<readonly models.File[]>(),
      reducers: {
        cancellation: ({ state }): State => ({
          ...state,
          finder: {
            ...state.finder,
            files: [],
            filesRequest: { error: undefined, isLoading: false },
          },
        }),
        error: ({ state, data: { error } }): State => ({
          ...state,
          finder: {
            ...state.finder,
            filesRequest: { error, isLoading: false },
          },
        }),
        request: ({ state }): State => ({
          ...state,
          finder: {
            ...state.finder,
            filesRequest: { error: undefined, isLoading: true },
          },
        }),
        success: ({ state, data: { response } }): State => ({
          ...state,
          finder: {
            ...state.finder,
            files: response,
            filesRequest: { error: undefined, isLoading: false },
          },
        }),
      },
    }),
    /**
     * Uploads a file to S3
     */
    upload: store.actions.request({
      input: store.actions.Data.asIs<models.FileRequest.ForUpload>(),
      output: store.actions.Data.asIs<void>(),
      reducers: {
        cancellation: ({ state, data: { request } }): State => {
          const newLookup = { ...state.uploadLookup }
          delete newLookup[models.FileRequest.getKey(request)]
          return {
            ...state,
            uploadLookup: newLookup,
          }
        },
        error: ({ state, data: { request, error } }): State => ({
          ...state,
          uploadLookup: {
            ...state.uploadLookup,
            [models.FileRequest.getKey(request)]: {
              error,
            },
          },
        }),
        request: ({ state, data }): State => ({
          ...state,
          uploadLookup: {
            ...state.uploadLookup,
            [models.FileRequest.getKey(data)]: { isUploading: true },
          },
        }),
        success: ({ state, data: { request } }): State => {
          const newLookup = { ...state.uploadLookup }
          delete newLookup[models.FileRequest.getKey(request)]
          return {
            ...state,
            uploadLookup: newLookup,
          }
        },
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}
